import React, { useState } from 'react';
import { Holding, Currency, HoldingType, ReturnType } from '../types';
import { Card, CardHeader, CardContent } from './ui/Card';
import { Button } from './ui/Button';
import { Input, Label, Select } from './ui/Forms';
import { PlusCircle, Save } from 'lucide-react';
import { COUNTRIES, CURRENCIES, getDefaultCurrency } from '../utils/countries';
interface HoldingsFormProps {
  onAdd: (holding: Omit<Holding, 'id' | 'createdAt' | 'history'>) => void;
  partnerNames: [string, string];
  baseCountry?: string;
  baseCurrency?: Currency;
  editingHolding?: Holding;
  onEdit?: (id: string, updates: Partial<Omit<Holding, 'id' | 'createdAt' | 'history'>>) => void;
  onCancelEdit?: () => void;
}
export function HoldingsForm({
  onAdd,
  partnerNames,
  baseCountry = 'US',
  baseCurrency = 'USD',
  editingHolding,
  onEdit,
  onCancelEdit
}: HoldingsFormProps) {
  const [person, setPerson] = useState<string>(editingHolding?.person || partnerNames[0]);
  const [name, setName] = useState(editingHolding?.name || '');
  const [amount, setAmount] = useState(editingHolding?.amount?.toString() || '');
  const [currency, setCurrency] = useState<Currency>(editingHolding?.currency || baseCurrency);
  const [type, setType] = useState<HoldingType>(editingHolding?.type || 'Liquid');
  const [country, setCountry] = useState<string>(editingHolding?.country || baseCountry);
  // Per-holding yield fields
  const [expectedReturn, setExpectedReturn] = useState(editingHolding?.expectedReturn?.toString() || '0');
  const [returnType, setReturnType] = useState<ReturnType>(editingHolding?.returnType || 'capital-growth');
  const [capitalGainsTaxRate, setCapitalGainsTaxRate] = useState(editingHolding?.capitalGainsTaxRate?.toString() || '0');
  // Illiquid drawdown settings
  const [drawdownAvailableYear, setDrawdownAvailableYear] = useState(editingHolding?.drawdownAvailableYear?.toString() || '0');
  const [yieldsAfterDrawdown, setYieldsAfterDrawdown] = useState(editingHolding?.yieldsAfterDrawdown ?? true);
  const handleCountryChange = (newCountry: string) => {
    setCountry(newCountry);
    const defaultCurr = getDefaultCurrency(newCountry);
    if (defaultCurr) {
      setCurrency(defaultCurr);
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !amount || isNaN(Number(amount))) return;
    const data: any = {
      person,
      name,
      amount: Number(amount),
      currency,
      type,
      country,
      expectedReturn: Number(expectedReturn) || 0,
      returnType,
      capitalGainsTaxRate: Number(capitalGainsTaxRate) || 0
    };
    // Only include illiquid drawdown fields for Illiquid type
    if (type === 'Illiquid') {
      data.drawdownAvailableYear = Number(drawdownAvailableYear) || 0;
      data.yieldsAfterDrawdown = yieldsAfterDrawdown;
    }
    if (editingHolding && onEdit) {
      onEdit(editingHolding.id, data);
    } else {
      onAdd(data);
      setName('');
      setAmount('');
      setExpectedReturn('0');
      setReturnType('capital-growth');
      setCapitalGainsTaxRate('0');
      setDrawdownAvailableYear('0');
      setYieldsAfterDrawdown(true);
    }
  };
  return <Card data-id="element-367">
      <CardHeader title={editingHolding ? 'Edit Holding' : 'Add New Holding'} subtitle={editingHolding ? 'Update all details for this asset' : 'Record a new cash asset or investment'} data-id="element-368" />
      <CardContent data-id="element-369">
        <form onSubmit={handleSubmit} className="space-y-4" data-id="element-370">
          <div className="grid grid-cols-2 gap-4" data-id="element-371">
            <div data-id="element-372">
              <Label data-id="element-373">Owner</Label>
              <Select value={person} onChange={e => setPerson(e.target.value)} options={[{
              value: partnerNames[0],
              label: partnerNames[0]
            }, {
              value: partnerNames[1],
              label: partnerNames[1]
            }, {
              value: 'Joint',
              label: '🤝 Mutual'
            }]} data-id="element-374" />
            </div>
            <div data-id="element-375">
              <Label data-id="element-376">Asset Type</Label>
              <Select value={type} onChange={e => setType(e.target.value as HoldingType)} options={[{
              value: 'Liquid',
              label: '💧 Liquid Cash'
            }, {
              value: 'Illiquid',
              label: '🏢 Illiquid Asset'
            }]} data-id="element-377" />
            </div>
          </div>

          <div data-id="element-378">
            <Label data-id="element-379">Country</Label>
            <Select searchable value={country} onChange={e => handleCountryChange(e.target.value)} options={COUNTRIES.map(c => ({
            value: c.code,
            label: `${c.flag} ${c.name}`
          }))} data-id="element-380" />
          </div>

          <div data-id="element-381">
            <Label data-id="element-382">Account / Asset Name</Label>
            <Input placeholder="e.g. Chase Checking, Vanguard ETF" value={name} onChange={e => setName(e.target.value)} required data-id="element-383" />
          </div>

          <div className="grid grid-cols-3 gap-4" data-id="element-384">
            <div className="col-span-2" data-id="element-385">
              <Label data-id="element-386">Amount</Label>
              <Input type="number" min="0" step="0.01" placeholder="0.00" value={amount} onChange={e => setAmount(e.target.value)} required data-id="element-387" />
            </div>
            <div data-id="element-388">
              <Label data-id="element-389">Currency</Label>
              <Select searchable value={currency} onChange={e => setCurrency(e.target.value as Currency)} options={CURRENCIES.map(c => ({
              value: c.code,
              label: `${c.flag} ${c.code} (${c.symbol})`
            }))} data-id="element-390" />
            </div>
          </div>

          <div className="border-t border-slate-100 pt-4 mt-2" data-id="element-391">
            <h4 className="text-sm font-semibold text-slate-800 mb-3" data-id="element-392">
              Yield & Returns
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-393">
              <div data-id="element-394">
                <Label data-id="element-395">Expected Return (%)</Label>
                <Input type="number" min="0" step="0.1" placeholder="0.0" value={expectedReturn} onChange={e => setExpectedReturn(e.target.value)} data-id="element-396" />
              </div>
              <div data-id="element-397">
                <Label data-id="element-398">Return Type</Label>
                <Select value={returnType} onChange={e => setReturnType(e.target.value as ReturnType)} options={[{
                value: 'capital-growth',
                label: '📈 Capital Growth'
              }, {
                value: 'dividend',
                label: '💸 Dividend'
              }, {
                value: 'interest',
                label: '🏦 Interest'
              }, {
                value: 'rental-yield',
                label: '🏠 Rental Yield'
              }, {
                value: 'none',
                label: 'None'
              }]} data-id="element-399" />
              </div>
              <div data-id="element-400">
                <Label data-id="element-401">Capital Gains Tax (%)</Label>
                <Input type="number" min="0" step="0.1" placeholder="0.0" value={capitalGainsTaxRate} onChange={e => setCapitalGainsTaxRate(e.target.value)} data-id="element-402" />
              </div>
            </div>
          </div>

          {/* Illiquid Drawdown Settings - NEW */}
          {type === 'Illiquid' && <div className="border-t border-slate-100 pt-4 mt-2" data-id="element-403">
              <h4 className="text-sm font-semibold text-slate-800 mb-3" data-id="element-404">
                Drawdown Availability
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-405">
                <div data-id="element-406">
                  <Label data-id="element-407">Available for Drawdown (Years from Now)</Label>
                  <Input type="number" min="0" step="1" placeholder="0" value={drawdownAvailableYear} onChange={e => setDrawdownAvailableYear(e.target.value)} data-id="element-408" />
                  <p className="text-xs text-slate-400 mt-1" data-id="element-409">
                    0 = immediately available at retirement
                  </p>
                </div>
                <div data-id="element-410">
                  <Label data-id="element-411">Continues Yielding After Drawdown?</Label>
                  <div className="flex bg-slate-100 p-1 rounded-lg mt-1" data-id="element-412">
                    <button type="button" className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${yieldsAfterDrawdown ? 'bg-white text-emerald-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} onClick={() => setYieldsAfterDrawdown(true)} data-id="element-413">
                      Yes — keeps earning
                    </button>
                    <button type="button" className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${!yieldsAfterDrawdown ? 'bg-white text-amber-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} onClick={() => setYieldsAfterDrawdown(false)} data-id="element-414">
                      No — liquidated
                    </button>
                  </div>
                  <p className="text-xs text-slate-400 mt-1" data-id="element-415">
                    {yieldsAfterDrawdown ? 'Asset continues generating returns during retirement' : 'Asset is sold/liquidated — no further returns'}
                  </p>
                </div>
              </div>
            </div>}

          <div className="flex gap-3 mt-4" data-id="element-416">
            <Button type="submit" className="flex-1" data-id="element-417">
              {editingHolding ? <>
                  <Save className="w-4 h-4 mr-2" data-id="element-418" />
                  Save Changes
                </> : <>
                  <PlusCircle className="w-4 h-4 mr-2" data-id="element-419" />
                  Add Holding
                </>}
            </Button>
            {editingHolding && onCancelEdit && <Button type="button" variant="secondary" onClick={onCancelEdit} data-id="element-420">
                Cancel
              </Button>}
          </div>
        </form>
      </CardContent>
    </Card>;
}